/**
 * One Chats conversation, live: the transcript the page renders, the socket
 * that keeps it current, and the two actions (send, stop) a reader takes.
 *
 * Two layers, separable on purpose, the way `useParseActivity` is:
 *
 *  - `foldChatMessage(transcript, message)` is **pure**. It takes what the
 *    socket (or a REST read) delivered and returns the next transcript. Items
 *    are keyed by `seq`, so an item delivered twice — once by the send's
 *    response, again by the socket; or again after a reconnect — lands once.
 *    A delta is applied only where it continues the text already shown for its
 *    `item_key`, so a repeat or a late, out-of-order one changes nothing, and
 *    the committed `assistant_message` with that key replaces the bubble.
 *
 *  - `useHgConversation(id)` owns the socket. It resumes with
 *    `items?after=<lastSeq>` and then watches from that seq, on first open and
 *    on every reconnect, so a dropped connection costs latency and never an
 *    item. Activity goes into the tracker `useParseActivity().beginParse`
 *    hands out, one record per turn under `-turnId` — the panel is the one the
 *    composer and Genie use, through the same fold.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import {
    cancelChatTurn,
    chatSocketUrl,
    decideChatProposal,
    extractQuotaError,
    fetchChatItems,
    HG_TURN_LIVE_STATUSES,
    sendChatMessage,
    type HgChatItem,
    type HgChatItemsPage,
    type HgChatSocketMessage,
    type HgChatTurn,
    type HgConversation,
    type LlmQuotaError,
} from '../api/client'
import { useParseActivity, type ParseActivity, type ParseTracker } from './useParseActivity'

// --- The fold ---------------------------------------------------------------

/** Text streaming into a bubble that has not been committed yet. */
interface HgStreaming {
    turnId: number
    itemKey: string
    text: string
}

/** A message the user sent that the store has not echoed back yet. */
interface HgPendingSend {
    clientKey: string
    text: string
}

export interface HgTranscript {
    conversation: HgConversation | null
    /** In `seq` order, one per seq. */
    items: HgChatItem[]
    lastSeq: number
    /** The conversation's latest turn. */
    turn: HgChatTurn | null
    /** Keyed by `item_key`. */
    streaming: Record<string, HgStreaming>
    /** `item_key`s already committed as items; a delta for one is stale. */
    committedKeys: string[]
    pending: HgPendingSend[]
    quotaError: LlmQuotaError | null
    /** A socket `error` code, e.g. `not_found`. */
    error: string | null
}

export const emptyTranscript = (): HgTranscript => ({
    conversation: null,
    items: [],
    lastSeq: 0,
    turn: null,
    streaming: {},
    committedKeys: [],
    pending: [],
    quotaError: null,
    error: null,
})

export const isTurnLive = (turn: HgChatTurn | null | undefined): boolean =>
    Boolean(turn && HG_TURN_LIVE_STATUSES.includes(turn.status))

const itemKeyOf = (item: HgChatItem): string | null => {
    const key = item.content?.item_key
    return typeof key === 'string' && key ? key : null
}

const withoutKey = <T,>(record: Record<string, T>, key: string): Record<string, T> => {
    if (!(key in record)) return record
    const next = { ...record }
    delete next[key]
    return next
}

/** The latest compaction summary among `items`, if any. */
const latestSummary = (items: HgChatItem[]): HgChatItem | undefined => {
    let latest: HgChatItem | undefined
    for (const i of items) {
        if (i.item_type === 'compaction_summary' && (!latest || i.seq > latest.seq)) latest = i
    }
    return latest
}

/** Whether `summary` replaced `item` on the page: everything at or below
 *  its `upto` except its `kept_ui_seqs`, and every earlier summary. The
 *  backend's `store.visible_items` is the same rule. */
const replacedBy = (summary: HgChatItem | undefined, item: HgChatItem): boolean => {
    if (!summary || item.seq === summary.seq) return false
    if (item.item_type === 'compaction_summary') return item.seq < summary.seq
    const upto = Number(summary.content?.upto ?? 0)
    const kept: number[] = summary.content?.kept_ui_seqs ?? []
    return item.seq <= upto && !kept.includes(item.seq)
}

const foldItem = (t: HgTranscript, item: HgChatItem): HgTranscript => {
    if (t.items.some((i) => i.seq === item.seq)) return t
    const summary = latestSummary([...t.items, item])
    if (replacedBy(summary, item)) return { ...t, lastSeq: Math.max(t.lastSeq, item.seq) }
    let items = [...t.items, item]
    if (item.item_type === 'compaction_summary') items = items.filter((i) => !replacedBy(item, i))
    // Almost always appended at the end; sorting covers the send's response
    // racing a socket backlog.
    if (t.items.length && t.items[t.items.length - 1].seq > item.seq) {
        items.sort((a, b) => a.seq - b.seq)
    }
    let { streaming, committedKeys, pending } = t
    const key = itemKeyOf(item)
    if (key) {
        streaming = withoutKey(streaming, key)
        if (!committedKeys.includes(key)) committedKeys = [...committedKeys, key]
    }
    const clientKey = item.content?.client_key
    if (typeof clientKey === 'string' && pending.some((p) => p.clientKey === clientKey)) {
        pending = pending.filter((p) => p.clientKey !== clientKey)
    }
    return {
        ...t,
        items,
        lastSeq: Math.max(t.lastSeq, item.seq),
        streaming,
        committedKeys,
        pending,
    }
}

const foldTurn = (t: HgTranscript, turn: HgChatTurn): HgTranscript => {
    if (t.turn && turn.id < t.turn.id) return t
    const newer = !t.turn || turn.id !== t.turn.id
    let streaming = t.streaming
    if (!isTurnLive(turn)) {
        // A turn that ended keeps only what it committed. Its items come
        // before its status on the socket, so nothing committed is lost here.
        streaming = Object.fromEntries(
            Object.entries(streaming).filter(([, s]) => s.turnId !== turn.id),
        )
    }
    return {
        ...t,
        turn,
        streaming,
        quotaError: newer ? null : t.quotaError,
    }
}

const foldDelta = (
    t: HgTranscript,
    turnId: number,
    itemKey: string,
    start: number,
    text: string,
): HgTranscript => {
    if (!itemKey || t.committedKeys.includes(itemKey)) return t
    const current = t.streaming[itemKey]?.text ?? ''
    // A gap: something before this never arrived, and the server will send the
    // whole partial again rather than this tail. A repeat: already shown.
    if (start < 0 || start > current.length || start + text.length <= current.length) return t
    return {
        ...t,
        streaming: {
            ...t.streaming,
            [itemKey]: { turnId, itemKey, text: current.slice(0, start) + text },
        },
    }
}

/** One socket message (or the pieces of a REST read), folded. Pure. */
export const foldChatMessage = (t: HgTranscript, message: HgChatSocketMessage): HgTranscript => {
    switch (message.type) {
        case 'conversation':
            return { ...t, conversation: message.conversation }
        case 'item':
            return foldItem(t, message.item)
        case 'turn':
            return foldTurn(t, message.turn)
        case 'delta':
            return foldDelta(t, message.turn_id, message.item_key, message.start, message.text)
        case 'quota_error': {
            const { scope, used, limit, window_seconds, message: text } = message
            return { ...t, quotaError: { scope, used, limit, window_seconds, message: text } }
        }
        case 'error':
            return { ...t, error: message.code }
        default:
            // `activity` goes to the tracker, `heartbeat` only keeps the line up.
            return t
    }
}

/** A REST read of the conversation — what a client resumes from. */
const foldChatPage = (t: HgTranscript, page: HgChatItemsPage): HgTranscript => {
    let next: HgTranscript = { ...t, conversation: page.conversation }
    for (const item of page.items) next = foldItem(next, item)
    if (page.latest_turn) next = foldTurn(next, page.latest_turn)
    return next
}

type Action =
    | { kind: 'reset' }
    | { kind: 'message'; message: HgChatSocketMessage }
    | { kind: 'page'; page: HgChatItemsPage }
    | { kind: 'pending'; send: HgPendingSend }
    | { kind: 'unpending'; clientKey: string }
    | { kind: 'quota'; quota: LlmQuotaError | null }

const reduce = (t: HgTranscript, action: Action): HgTranscript => {
    switch (action.kind) {
        case 'reset':
            return emptyTranscript()
        case 'message':
            return foldChatMessage(t, action.message)
        case 'page':
            return foldChatPage(t, action.page)
        case 'pending':
            return { ...t, pending: [...t.pending, action.send] }
        case 'unpending':
            return { ...t, pending: t.pending.filter((p) => p.clientKey !== action.clientKey) }
        case 'quota':
            return { ...t, quotaError: action.quota }
    }
}

// --- The hook ---------------------------------------------------------------

/** The record key a turn's activity is folded under. Negative, so it can never
 *  be a check-in id in the same `useParseActivity`. */
export const turnRecordKey = (turnId: number): number => -turnId

const RECONNECT_DELAYS_MS = [500, 1000, 2000, 5000, 10000]

export const newClientKey = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

export interface UseHgConversationResult {
    transcript: HgTranscript
    /** The latest turn's activity record, if it has one. */
    activity: ParseActivity | null
    setActivityExpanded: (expanded: boolean) => void
    setActivityTechnical: (technical: boolean) => void
    /** Resolves false when the send failed, so the composer can keep the text. */
    send: (text: string) => Promise<boolean>
    stop: () => Promise<void>
    /** Approve (a draft in part, by its item keys) or reject a proposal.
     *  Resolves false when the decision did not go through. */
    decide: (proposalId: number, decision: 'approve' | 'reject', subset?: string[]) => Promise<boolean>
    /** A Stop was asked for and the turn has not ended yet. */
    stopping: boolean
    /** The last action failed; one short line for the page. */
    actionError: string | null
    clearActionError: () => void
    clearQuotaError: () => void
    /** A REST response's copy of the conversation (its advisors changed). */
    setConversation: (conversation: HgConversation) => void
}

export const useHgConversation = (conversationId: number | null): UseHgConversationResult => {
    const [transcript, dispatch] = useReducer(reduce, undefined, emptyTranscript)
    const { activities, beginParse, setExpanded, setTechnical } = useParseActivity()
    const [stoppingTurnId, setStoppingTurnId] = useState<number | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)

    // Read inside socket handlers, which outlive any one render.
    const lastSeqRef = useRef(0)
    const trackerRef = useRef<{ turnId: number; tracker: ParseTracker } | null>(null)
    const activitiesRef = useRef(activities)
    activitiesRef.current = activities
    const beginParseRef = useRef(beginParse)
    beginParseRef.current = beginParse
    const setExpandedRef = useRef(setExpanded)
    setExpandedRef.current = setExpanded
    const turnRef = useRef<HgChatTurn | null>(null)
    turnRef.current = transcript.turn

    useEffect(() => {
        dispatch({ kind: 'reset' })
        lastSeqRef.current = 0
        trackerRef.current?.tracker.end()
        trackerRef.current = null
        setStoppingTurnId(null)
        setActionError(null)
        if (conversationId == null) return

        let disposed = false
        let fatal = false
        let socket: WebSocket | null = null
        let attempt = 0
        let timer: ReturnType<typeof setTimeout> | null = null

        const apply = (message: HgChatSocketMessage) => {
            if (message.type === 'item' && message.item.seq > lastSeqRef.current) {
                lastSeqRef.current = message.item.seq
            }
            dispatch({ kind: 'message', message })
        }

        /**
         * A fresh record for `turnId`. Every new socket replays the latest
         * turn's activity from its first event, so the record restarts with
         * it rather than folding the same events twice; whether it was open
         * survives the restart.
         */
        const beginTurnRecord = (turnId: number) => {
            const key = turnRecordKey(turnId)
            const wasExpanded = activitiesRef.current[key]?.expanded ?? false
            if (trackerRef.current && trackerRef.current.turnId !== turnId) {
                trackerRef.current.tracker.end()
            }
            trackerRef.current = { turnId, tracker: beginParseRef.current(key) }
            if (wasExpanded) setExpandedRef.current(key, true)
        }

        const scheduleReconnect = () => {
            if (disposed || fatal || timer) return
            const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]
            attempt += 1
            timer = setTimeout(() => {
                timer = null
                void connect()
            }, delay)
        }

        const connect = async () => {
            let page: HgChatItemsPage
            try {
                page = await fetchChatItems(conversationId, lastSeqRef.current)
            } catch (err: any) {
                if (disposed) return
                if (err?.response?.status === 404) {
                    fatal = true
                    dispatch({ kind: 'message', message: { type: 'error', code: 'not_found' } })
                    return
                }
                scheduleReconnect()
                return
            }
            if (disposed) return
            for (const item of page.items) {
                if (item.seq > lastSeqRef.current) lastSeqRef.current = item.seq
            }
            dispatch({ kind: 'page', page })

            const ws = new WebSocket(chatSocketUrl(conversationId, lastSeqRef.current))
            socket = ws
            let turnSeen = false
            ws.onopen = () => {
                attempt = 0
            }
            ws.onmessage = (event) => {
                if (disposed || socket !== ws) return
                let message: HgChatSocketMessage
                try {
                    message = JSON.parse(event.data)
                } catch {
                    return
                }
                if (message.type === 'turn') {
                    if (!turnSeen || trackerRef.current?.turnId !== message.turn.id) {
                        beginTurnRecord(message.turn.id)
                    }
                    turnSeen = true
                } else if (message.type === 'activity') {
                    trackerRef.current?.tracker.onActivity(message.event)
                    return
                } else if (message.type === 'error' && message.code === 'not_found') {
                    fatal = true
                }
                apply(message)
            }
            ws.onclose = () => {
                if (socket === ws) socket = null
                scheduleReconnect()
            }
        }

        void connect()

        return () => {
            disposed = true
            if (timer) clearTimeout(timer)
            if (socket) {
                const ws = socket
                socket = null
                try {
                    ws.close()
                } catch {
                    /* already closed */
                }
            }
        }
    }, [conversationId])

    const send = useCallback(
        async (text: string): Promise<boolean> => {
            const body = text.trim()
            if (conversationId == null || !body) return false
            const clientKey = newClientKey()
            setActionError(null)
            dispatch({ kind: 'pending', send: { clientKey, text: body } })
            try {
                const sent = await sendChatMessage(conversationId, body, clientKey)
                dispatch({ kind: 'message', message: { type: 'item', item: sent.item } })
                if (sent.item.seq > lastSeqRef.current) lastSeqRef.current = sent.item.seq
                return true
            } catch (err) {
                dispatch({ kind: 'unpending', clientKey })
                const quota = extractQuotaError(err)
                if (quota) dispatch({ kind: 'quota', quota })
                else setActionError("Couldn't send. Try again.")
                return false
            }
        },
        [conversationId],
    )

    const stop = useCallback(async () => {
        const turn = turnRef.current
        if (!turn || !isTurnLive(turn)) return
        // Shown at once: the server stops a running turn at its next
        // heartbeat, which can be seconds away.
        setStoppingTurnId(turn.id)
        setActionError(null)
        try {
            const result = await cancelChatTurn(turn.id)
            dispatch({ kind: 'message', message: { type: 'turn', turn: result.turn } })
            if (!result.cancelled && isTurnLive(result.turn)) {
                setStoppingTurnId(null)
                setActionError("Couldn't stop it. Try again.")
            }
        } catch {
            setStoppingTurnId(null)
            setActionError("Couldn't stop it. Try again.")
        }
    }, [])

    const decide = useCallback(
        async (proposalId: number, decision: 'approve' | 'reject', subset?: string[]) => {
            try {
                const result = await decideChatProposal(proposalId, decision, subset)
                // The decision item, at once: the card shows it before the
                // socket echoes it, and the echo lands on the same seq.
                dispatch({ kind: 'message', message: { type: 'item', item: result.item } })
                if (result.item.seq > lastSeqRef.current) lastSeqRef.current = result.item.seq
                return true
            } catch (err: any) {
                // Decided already (another tab, a double click): the socket
                // brings the item that says how.
                return err?.response?.status === 409
            }
        },
        [],
    )

    const turn = transcript.turn
    const activity = turn ? activities[turnRecordKey(turn.id)] ?? null : null
    const stopping = turn != null && stoppingTurnId === turn.id && isTurnLive(turn)

    const setActivityExpanded = useCallback(
        (expanded: boolean) => {
            const current = turnRef.current
            if (current) setExpanded(turnRecordKey(current.id), expanded)
        },
        [setExpanded],
    )
    const setActivityTechnical = useCallback(
        (technical: boolean) => {
            const current = turnRef.current
            if (current) setTechnical(turnRecordKey(current.id), technical)
        },
        [setTechnical],
    )
    const clearActionError = useCallback(() => setActionError(null), [])
    const clearQuotaError = useCallback(() => dispatch({ kind: 'quota', quota: null }), [])
    const setConversation = useCallback(
        (conversation: HgConversation) =>
            dispatch({ kind: 'message', message: { type: 'conversation', conversation } }),
        [],
    )

    return {
        transcript,
        activity,
        setActivityExpanded,
        setActivityTechnical,
        send,
        stop,
        decide,
        stopping,
        actionError,
        clearActionError,
        clearQuotaError,
        setConversation,
    }
}
